import { el } from '../../dom';
import { playSfx } from '../../audio/audio';
import { chromeBottom } from '../../ui/panel';
import { BUILDINGS, type FarmCtx } from './context';
import { plotsDestroyedBy } from './placement';

/**
 * "This will destroy N beds" — in the GAME, not in the browser.
 *
 * OWNER (2026-08-17), two instructions in sequence: "no need to remove
 * confirmation, we are informing user", then "but we make ingame confirmation,
 * not via chrome".
 *
 * The first version called window.confirm(). That is wrong here for reasons
 * beyond looks: a native dialog is chrome-styled with the site's origin in it, it
 * is not themable, on a phone it lands as a system sheet over a pixel-art game,
 * and it BLOCKS the main thread — the farm's ticker stops dead while it is open,
 * so the world freezes mid-animation behind it. Every other decision in this game
 * is asked with a painted card (see confirmDemolish, which this deliberately
 * mirrors down to the arming delay).
 *
 * The dialog INFORMS rather than gates: it says exactly what the tap costs, and
 * distinguishes a bed you spent seconds hoeing from a crop you paid a seed for and
 * waited hours on, so a routine placement cannot be mistaken for a ruined harvest.
 */

/** Live dialog, so a second placement tap cannot stack two of them. */
let overlay: HTMLElement | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;
let armTimer = 0;

export function closePlotLossConfirm(): void {
  window.clearTimeout(armTimer);
  if (escHandler) { window.removeEventListener('keydown', escHandler, true); escHandler = null; }
  overlay?.remove();
  overlay = null;
}

/**
 * What the card says, as a pure function so the wording is testable — jsdom can
 * build the DOM but cannot tell you whether the sentence reads correctly.
 *
 * Empty beds and planted ones are counted separately because they are not the
 * same loss, and a message that said "3 beds" for two bare squares and one ripe
 * wheat would bury the only part worth reading.
 */
export function plotLossMessage(planted: number, empty: number): string {
  const parts = [
    planted ? `${planted} growing crop${planted === 1 ? '' : 's'}` : '',
    empty ? `${empty} empty bed${empty === 1 ? '' : 's'}` : '',
  ].filter(Boolean);
  return `This will destroy ${parts.join(' and ')}.`;
}

/**
 * Ask before a placement deletes beds. `onConfirm` runs only if the player agrees.
 *
 * Callback rather than a boolean return: a painted dialog cannot answer
 * synchronously the way window.confirm() did, so the placement moved to the other
 * side of the answer. Callers must therefore treat "asked" as handled and do
 * nothing more on that tap.
 */
export function confirmPlotLoss(
  c: FarmCtx,
  kind: string,
  tx: number,
  ty: number,
  onConfirm: () => void,
): void {
  closePlotLossConfirm();
  playSfx('ui_open');

  const doomed = plotsDestroyedBy(c.farm?.plots ?? [], kind, tx, ty);
  const planted = doomed.filter((p) => p.state === 'PLANTED' && p.cropId).length;
  const name = BUILDINGS[kind]?.name ?? kind;

  // ARMING DELAY, the same reason confirmDemolish has one: this card is created
  // inside the pointertap handler and lands where the player just tapped the map.
  // Placing buildings is a tapping activity, so click 2 of a natural double-tap
  // would otherwise land straight on "Place it" and destroy the beds in one
  // gesture — the accident the dialog exists to prevent.
  const ARM_MS = 400;
  const armedAt = Date.now() + ARM_MS;
  const danger = el('button',
    'w-full min-h-[44px] px-3 py-2 mb-2 rounded bg-error/20 border border-error text-error hover:bg-error/30 opacity-50 pointer-events-none',
    { onClick: () => { if (Date.now() < armedAt) return; closePlotLossConfirm(); onConfirm(); } },
    `Place the ${name} anyway`);
  armTimer = window.setTimeout(() => {
    danger.classList.remove('opacity-50', 'pointer-events-none');
  }, ARM_MS);
  const cancel = el('button', 'w-full min-h-[44px] px-3 py-2 rounded border border-border text-text-dim hover:text-text',
    { onClick: () => closePlotLossConfirm() }, 'Leave the beds');

  const card = el('div',
    'bg-surface border border-border rounded-lg p-5 w-[320px] max-w-[90vw] shadow-lift text-center sheet-card', {},
    el('h3', 'font-heading text-gold mb-1', {}, `${name} over your beds?`),
    el('p', 'text-sm text-text-dim mb-2', {}, plotLossMessage(planted, doomed.length - planted)),
    el('p', 'text-[11px] text-text-dim mb-3', {},
      'The building covers them and the ground goes back to grass.'),
    danger, cancel);

  const top = chromeBottom() + 8;
  overlay = el('div', 'fixed inset-x-0 bottom-0 z-[70] flex items-start justify-center',
    { style: `top:${top}px`, onClick: (e) => { if (e.target === overlay) closePlotLossConfirm(); } },
    el('div', 'mt-[8vh]', {}, card));
  document.body.appendChild(overlay);
  cancel.focus();                       // keeping the beds is the safe default
  escHandler = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();                // not "close build mode" as well
    closePlotLossConfirm();
  };
  window.addEventListener('keydown', escHandler, true);
}
